import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { api, formatApiError } from "../lib/api";
import {
  Heart, Save, Eye, Send, ChevronLeft, Plus, Trash2,
  Server, Sparkles, ShieldCheck, RotateCcw, CheckCircle2, CircleAlert, History, Undo2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * /admin/support-page — Admin-only CMS for the /support page copy.
 *
 * Editable: page title/subtitle, donation section labels, "Why give" bullets,
 * thank-you copy, supporter badge blurb, footer note, and a PayPal on/off
 * toggle. Save Draft stashes changes; Publish rolls them out. Preview opens
 * /support?preview=draft in a new tab so admins can eyeball layout before
 * publishing. Payment amounts, presets, Venmo, and badge logic are NOT
 * editable here — that lives under Admin → Billing & Payments.
 */

const ICON_OPTIONS = [
  { key: "server",   label: "Server",     Icon: Server },
  { key: "sparkles", label: "Sparkles",   Icon: Sparkles },
  { key: "shield",   label: "Shield",     Icon: ShieldCheck },
];

const EMPTY_ITEM = { icon: "server", title: "", body: "" };

export default function AdminSupportPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState({ published_at: null, draft_updated_at: null, has_unpublished_changes: false });
  const [history, setHistory] = useState([]);
  const [rollingBackId, setRollingBackId] = useState(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/support-page/history");
      setHistory(data.items || []);
    } catch {
      // history is optional — silence errors so the editor keeps working
      setHistory([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support-page");
      setDraft(data.draft);
      setMeta({
        published_at: data.published_at,
        draft_updated_at: data.draft_updated_at,
        has_unpublished_changes: !!data.has_unpublished_changes,
      });
      await loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setItem = (i, patch) => setDraft((d) => ({
    ...d,
    why_give_items: d.why_give_items.map((it, idx) => idx === i ? { ...it, ...patch } : it),
  }));
  const addItem = () => setDraft((d) => ({
    ...d,
    why_give_items: [...(d.why_give_items || []), { ...EMPTY_ITEM }],
  }));
  const removeItem = (i) => setDraft((d) => ({
    ...d,
    why_give_items: d.why_give_items.filter((_, idx) => idx !== i),
  }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/support-page", draft);
      toast.success("Draft saved");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!window.confirm("Publish these changes to the live Support page?")) return;
    setPublishing(true);
    try {
      // Save draft first so any un-saved edits land in the DB before publish.
      await api.patch("/admin/support-page", draft);
      await api.post("/admin/support-page/publish");
      toast.success("Published — members now see the updated wording");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPublishing(false); }
  };

  const revert = async () => {
    if (!window.confirm("Discard your draft and reload the published version?")) return;
    setReverting(true);
    try {
      await api.post("/admin/support-page/revert");
      toast.success("Draft reverted to published version");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReverting(false); }
  };

  const fmtWhen = (iso) => {
    if (!iso) return "never";
    try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
  };

  const rollback = async (entry) => {
    if (!window.confirm(`Roll back the live Support page to "${entry.page_title || "(untitled)"}" published on ${fmtWhen(entry.published_at)}?\n\nThis restores that snapshot for members immediately.`)) return;
    setRollingBackId(entry.id);
    try {
      await api.post(`/admin/support-page/rollback/${entry.id}`);
      toast.success("Rolled back — members now see the restored copy");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRollingBackId(null); }
  };

  const preview = () => {
    window.open("/support?preview=draft", "_blank", "noopener,noreferrer");
  };

  if (loading || !draft) {
    return (
      <AppLayout>
        <div className="p-8 text-slate-500 font-mono-num text-sm">Loading Support Page editor…</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid="admin-support-page-editor" className="max-w-4xl mx-auto">
        <div className="mb-4">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-slate-500 hover:text-teal-700 text-xs font-mono-num uppercase tracking-widest"
            data-testid="admin-support-back"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Admin Settings
          </Link>
        </div>

        <PageHero
          eyebrow="Content Editor"
          title="Support Page"
          subtitle="Edit every visible line on the /support page. Save Draft to stash changes, Preview to see the draft, Publish when you're ready for members to see it."
          icon={<Heart className="w-8 h-8 text-teal-600" />}
          gradient
        />

        {/* Status strip */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="support-editor-status">
          {meta.has_unpublished_changes ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-semibold px-3 py-1">
              <CircleAlert className="w-3.5 h-3.5" /> Unpublished changes
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 text-teal-700 text-[11px] font-semibold px-3 py-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Draft matches published
            </span>
          )}
          <span className="text-[11px] font-mono-num text-slate-500 uppercase tracking-widest">
            · Published: {fmtWhen(meta.published_at)}
          </span>
          <span className="text-[11px] font-mono-num text-slate-500 uppercase tracking-widest">
            · Draft saved: {fmtWhen(meta.draft_updated_at)}
          </span>
        </div>

        {/* Action bar — sticky at the bottom on mobile, top on desktop */}
        <div className="sticky top-0 z-20 -mx-4 sm:mx-0 mb-6 px-4 py-3 bg-white/95 backdrop-blur border-b border-slate-200 flex flex-wrap items-center gap-2" data-testid="support-editor-actions">
          <Button onClick={saveDraft} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs uppercase tracking-widest font-semibold h-10 px-4" data-testid="support-editor-save-draft">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save Draft"}
          </Button>
          <Button onClick={preview} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50 rounded-lg text-xs uppercase tracking-widest font-semibold h-10 px-4" data-testid="support-editor-preview">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
          </Button>
          <Button onClick={publish} disabled={publishing} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs uppercase tracking-widest font-semibold h-10 px-4" data-testid="support-editor-publish">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {publishing ? "Publishing…" : "Publish"}
          </Button>
          <Button onClick={revert} disabled={reverting} variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg text-xs uppercase tracking-widest font-semibold h-10 px-4" data-testid="support-editor-revert">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Revert
          </Button>
        </div>

        <div className="space-y-5">
          {/* PayPal toggle */}
          <Card className="border border-slate-200 rounded-xl p-5" data-testid="support-editor-paypal-card">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900 mb-1">Show PayPal section</div>
                <div className="text-xs text-slate-600 leading-relaxed">
                  When off, the "PayPal &amp; cards" footer notice (and any future PayPal donation UI) is hidden from members. Venmo, donation amounts, and Supporter badges are unaffected.
                </div>
              </div>
              <Switch
                checked={!!draft.paypal_enabled}
                onCheckedChange={(v) => set({ paypal_enabled: !!v })}
                data-testid="support-editor-paypal-toggle"
              />
            </div>
          </Card>

          {/* Hero */}
          <Card className="border border-slate-200 rounded-xl p-5 space-y-4" data-testid="support-editor-hero-card">
            <SectionHeading>Page hero</SectionHeading>
            <Field label="Page title" testId="support-editor-title">
              <Input value={draft.page_title} onChange={(e) => set({ page_title: e.target.value })} maxLength={120} />
            </Field>
            <Field label="Introduction / subtitle" testId="support-editor-subtitle">
              <Textarea value={draft.page_subtitle} onChange={(e) => set({ page_subtitle: e.target.value })} rows={3} maxLength={280} />
            </Field>
          </Card>

          {/* Donation section labels */}
          <Card className="border border-slate-200 rounded-xl p-5 space-y-4" data-testid="support-editor-donation-card">
            <SectionHeading>Donation section labels</SectionHeading>
            <Field label='Eyebrow above amount presets (e.g. "Pick an amount")' testId="support-editor-donation-eyebrow">
              <Input value={draft.donation_eyebrow} onChange={(e) => set({ donation_eyebrow: e.target.value })} maxLength={60} />
            </Field>
            <Field label='Label above the total row (e.g. "You are giving")' testId="support-editor-donation-amount-label">
              <Input value={draft.donation_amount_label} onChange={(e) => set({ donation_amount_label: e.target.value })} maxLength={80} />
            </Field>
            <Field label="Hint text under the Venmo button" testId="support-editor-donation-cta-hint">
              <Textarea value={draft.donation_cta_hint} onChange={(e) => set({ donation_cta_hint: e.target.value })} rows={2} maxLength={280} />
            </Field>
            <Field label="Footer notice (shown only when PayPal is ON)" testId="support-editor-footer-note">
              <Textarea value={draft.footer_note} onChange={(e) => set({ footer_note: e.target.value })} rows={2} maxLength={280} disabled={!draft.paypal_enabled} />
              {!draft.paypal_enabled && (
                <div className="text-[11px] text-slate-500 mt-1">Turn on PayPal above to show this notice.</div>
              )}
            </Field>
          </Card>

          {/* Why give */}
          <Card className="border border-slate-200 rounded-xl p-5 space-y-4" data-testid="support-editor-why-card">
            <SectionHeading>Why donations matter</SectionHeading>
            <Field label='Section eyebrow (e.g. "Why give")' testId="support-editor-why-eyebrow">
              <Input value={draft.why_give_eyebrow} onChange={(e) => set({ why_give_eyebrow: e.target.value })} maxLength={60} />
            </Field>
            <Field label='Section heading (e.g. "Where your dollars go")' testId="support-editor-why-heading">
              <Input value={draft.why_give_heading} onChange={(e) => set({ why_give_heading: e.target.value })} maxLength={120} />
            </Field>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold mb-2 block">How support is used — bullets</Label>
              <div className="space-y-3">
                {(draft.why_give_items || []).map((item, i) => (
                  <div key={`why-${i}`} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`support-editor-why-item-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <div className="text-[10px] font-mono-num uppercase tracking-widest text-slate-500">Bullet {i + 1}</div>
                      <div className="flex items-center gap-1">
                        {ICON_OPTIONS.map(({ key, Icon }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setItem(i, { icon: key })}
                            className={`w-8 h-8 rounded-md border transition-colors flex items-center justify-center ${item.icon === key ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-200 bg-white text-slate-500 hover:border-teal-300"}`}
                            aria-label={`Set icon to ${key}`}
                            data-testid={`support-editor-why-icon-${i}-${key}`}
                          >
                            <Icon className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="ml-auto text-slate-400 hover:text-red-600 p-1.5"
                        aria-label="Remove bullet"
                        data-testid={`support-editor-why-remove-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Input
                      placeholder="Bold lead-in (e.g. Servers & database.)"
                      value={item.title}
                      onChange={(e) => setItem(i, { title: e.target.value })}
                      className="mb-2"
                      maxLength={80}
                      data-testid={`support-editor-why-title-${i}`}
                    />
                    <Textarea
                      placeholder="Body copy for this bullet"
                      value={item.body}
                      onChange={(e) => setItem(i, { body: e.target.value })}
                      rows={2}
                      maxLength={280}
                      data-testid={`support-editor-why-body-${i}`}
                    />
                  </div>
                ))}
              </div>
              <Button
                type="button"
                onClick={addItem}
                variant="outline"
                className="mt-3 h-9 border-dashed border-slate-300 text-slate-600 hover:border-teal-400 hover:text-teal-700 rounded-lg"
                data-testid="support-editor-why-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add bullet
              </Button>
            </div>
          </Card>

          {/* Thank-you */}
          <Card className="border border-slate-200 rounded-xl p-5 space-y-4" data-testid="support-editor-thanks-card">
            <SectionHeading>Thank-you screen</SectionHeading>
            <Field label='Title after a donation (use "{name}" to insert first name)' testId="support-editor-thanks-title">
              <Input value={draft.thank_you_title} onChange={(e) => set({ thank_you_title: e.target.value })} maxLength={120} />
            </Field>
            <Field label="Body copy shown under the total" testId="support-editor-thanks-body">
              <Textarea value={draft.thank_you_body} onChange={(e) => set({ thank_you_body: e.target.value })} rows={2} maxLength={280} />
            </Field>
          </Card>

          {/* Supporter badge */}
          <Card className="border border-slate-200 rounded-xl p-5 space-y-4" data-testid="support-editor-badge-card">
            <SectionHeading>Supporter badge callout</SectionHeading>
            <Field label='Callout title (e.g. "Supporter badge")' testId="support-editor-badge-title">
              <Input value={draft.supporter_badge_title} onChange={(e) => set({ supporter_badge_title: e.target.value })} maxLength={60} />
            </Field>
            <Field label="Callout body" testId="support-editor-badge-body">
              <Textarea value={draft.supporter_badge_body} onChange={(e) => set({ supporter_badge_body: e.target.value })} rows={3} maxLength={400} />
            </Field>
          </Card>

          {/* Publish history */}
          <Card className="border border-slate-200 rounded-xl p-5 space-y-4" data-testid="support-editor-history-card">
            <SectionHeading>
              <span className="inline-flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" /> Publish history · last {history.length || 0}
              </span>
            </SectionHeading>
            <div className="text-xs text-slate-600 leading-relaxed">
              Every publish is snapshotted here. If a copy change lands wrong, roll back to any prior version in one click — no retyping. History is capped at the 20 most recent publishes.
            </div>
            {history.length === 0 ? (
              <div className="border border-dashed border-slate-200 bg-slate-50 rounded-lg p-6 text-center text-xs font-mono-num uppercase tracking-widest text-slate-400" data-testid="support-editor-history-empty">
                No publishes yet — hit Publish above to snapshot the current draft.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden" data-testid="support-editor-history-list">
                {history.map((h, idx) => {
                  const isLatest = idx === 0;
                  const isRollback = h.kind === "rollback";
                  return (
                    <li
                      key={h.id}
                      className="flex flex-wrap items-center gap-3 px-3 py-2.5 bg-white"
                      data-testid={`support-editor-history-row-${h.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-semibold text-slate-900 truncate">
                            {h.page_title || "(untitled)"}
                          </span>
                          {isLatest && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 text-teal-700 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5">
                              <CheckCircle2 className="w-3 h-3" /> Live
                            </span>
                          )}
                          {isRollback && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5">
                              <Undo2 className="w-3 h-3" /> Rollback
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono-num text-slate-500 mt-0.5">
                          {fmtWhen(h.published_at)} · {h.actor_name} · {h.why_give_count} bullet{h.why_give_count === 1 ? "" : "s"}
                        </div>
                      </div>
                      <Button
                        onClick={() => rollback(h)}
                        disabled={isLatest || rollingBackId === h.id}
                        variant="outline"
                        className="border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 rounded-lg text-[11px] uppercase tracking-widest font-semibold h-8 px-3"
                        data-testid={`support-editor-history-rollback-${h.id}`}
                      >
                        <Undo2 className="w-3 h-3 mr-1.5" />
                        {rollingBackId === h.id ? "Rolling back…" : isLatest ? "Current" : "Roll back"}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        <div className="h-12" />
      </div>
    </AppLayout>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="text-[11px] font-mono-num uppercase tracking-[0.3em] text-teal-700 border-b border-slate-100 pb-2">
      {children}
    </div>
  );
}

function Field({ label, testId, children }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold">{label}</Label>
      {children}
    </div>
  );
}
